import { api } from '@/lib/api'

export interface CounselorResource {
  id: string
  title: string
  description?: string
  content: string
  isPublished: boolean
  order: number
  lastModifiedBy: string
  createdAt: string
  updatedAt: string
}

export interface ResourceListResponse {
  resources: CounselorResource[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface ResourceQueryParams {
  page?: number
  limit?: number
  search?: string
  published?: boolean
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

export interface CreateResourceData {
  title: string
  description?: string
  content: string
  isPublished: boolean
  order: number
}

export interface UpdateResourceData {
  title?: string
  description?: string
  content?: string
  isPublished?: boolean
  order?: number
}

export class AdminCounselorResourceService {
  /**
   * Get list of all counselor resources (admin view)
   */
  static async getResources(params: ResourceQueryParams = {}): Promise<ResourceListResponse> {
    const queryParams = new URLSearchParams()

    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.search) queryParams.append('search', params.search)
    if (params.published !== undefined) queryParams.append('published', params.published.toString())
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)

    const query = queryParams.toString()
    const endpoint = query ? `/admin/counselor-resources?${query}` : '/admin/counselor-resources'

    return await api.get<ResourceListResponse>(endpoint)
  }

  /**
   * Get a specific counselor resource by ID (admin view)
   */
  static async getResource(id: string): Promise<CounselorResource> {
    return await api.get<CounselorResource>(`/admin/counselor-resources/${id}`)
  }

  /**
   * Create a new counselor resource
   */
  static async createResource(data: CreateResourceData): Promise<CounselorResource> {
    return await api.post<CounselorResource>('/admin/counselor-resources', data)
  }

  /**
   * Update an existing counselor resource
   */
  static async updateResource(id: string, data: UpdateResourceData): Promise<CounselorResource> {
    return await api.put<CounselorResource>(`/admin/counselor-resources/${id}`, data)
  }

  /**
   * Delete a counselor resource
   */
  static async deleteResource(id: string): Promise<void> {
    return await api.delete(`/admin/counselor-resources/${id}`)
  }

  /**
   * Toggle publish status of a counselor resource
   */
  static async togglePublishStatus(id: string, isPublished: boolean): Promise<CounselorResource> {
    return await api.patch<CounselorResource>(`/admin/counselor-resources/${id}/publish`, {
      isPublished
    })
  }

  /**
   * Reorder counselor resources
   */
  static async reorderResources(resourceIds: string[]): Promise<void> {
    return await api.post('/admin/counselor-resources/reorder', { resourceIds })
  }
}